import { FC, useCallback, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import {
  simpleBugoBrandListDataAtom,
  simpleBugoBrandListStatusAtom,
} from '@shared/api/bugoBrand/bugoBrand.atom';
import { useSimpleBugoBrandListHook } from '@shared/api/bugoBrand/bugoBrand.hook';
import {
  getDashboardCoffinOutDaily,
  getDashboardCoffinOutPacing,
} from '@shared/api/giftOrder/giftOrder.controller';
import {
  CoffinOutDailyResponse,
  CoffinOutDailyRow,
  CoffinOutPacingResponse,
} from '@shared/api/giftOrder/giftOrderDashboard.interface';

import {
  CenterLoading,
  ErrorBox,
  TD_CLASS,
  TH_CLASS,
} from '../../components/DashboardShared';
import { PACING_CACHE_TTL_MS, useCachedResource } from './analysisCache';
import { CoffinOutCorrelation } from './CoffinOutCorrelation';
import { CoffinOutPacing } from './CoffinOutPacing';
import {
  addMonths,
  daysInMonth,
  fmt,
  fmtWonCompact,
  getLocalToday,
  getNiceScale,
  parseYm,
  toDateStr,
  ymOf,
} from './analysisUtils';

type ViewMode = 'calendar' | 'trend' | 'table' | 'corr' | 'pace';
type HeatMetric = 'fevent' | 'wreath' | 'gift';

const WEEKDAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

const VIEW_LABELS: { key: ViewMode; label: string }[] = [
  { key: 'calendar', label: '달력' },
  { key: 'trend', label: '추이' },
  { key: 'corr', label: '상관 분석' },
  { key: 'pace', label: '판매 페이스' },
  { key: 'table', label: '표' },
];

const HEAT_METRICS: { key: HeatMetric; label: string }[] = [
  { key: 'fevent', label: '상가 수' },
  { key: 'wreath', label: '화환 매출' },
  { key: 'gift', label: '답례품 매출' },
];

// 달력 히트 색 — 프로젝트 차트 기본색(indigo #6366f1)의 단일 색조 순차 램프
const HEAT_RGB = '99, 102, 241';

const emptyRow = (date: string): CoffinOutDailyRow => ({
  date,
  feventCount: 0,
  sharedFeventCount: 0,
  wreathAmount: 0,
  wreathCount: 0,
  giftAmount: 0,
  giftQuantity: 0,
  giftOrderCount: 0,
});

const weekdayColor = (wd: number): string =>
  wd === 0 ? 'text-red-500' : wd === 6 ? 'text-blue-500' : 'text-gray-500';

interface DayCell extends CoffinOutDailyRow {
  day: number;
  weekday: number;
}

/**
 * 발인일 통계 탭 — 발인일(coffinOut, KST) 기준으로 하루에 상가가 몇 곳이었고
 * 그 상가들에서 화환·답례품이 얼마나 팔렸는지. 매출은 주문일이 아니라
 * 발인일로 귀속된다 (coffin-out-daily API).
 * 스펙: docs/gift-coffin-out-daily-stats-spec.md
 */
export const CoffinOutTab: FC = function CoffinOutTab() {
  const [ym, setYm] = useState(() => ymOf(new Date()));
  const [brandId, setBrandId] = useState('');
  const [teamType, setTeamType] = useState('');
  const [view, setView] = useState<ViewMode>('calendar');
  const [heatMetric, setHeatMetric] = useState<HeatMetric>('fevent');

  // 상조 목록 (필터용)
  useSimpleBugoBrandListHook();
  const brandListStatus = useRecoilValue(simpleBugoBrandListStatusAtom);
  const brandList = useRecoilValue(simpleBugoBrandListDataAtom);
  const selectedBrand = useMemo(
    () => brandList?.find((b) => b._id === brandId),
    [brandList, brandId],
  );
  const teamTypes = selectedBrand?.bugoAgency?.teamTypes ?? [];

  //* 같은 (월·상조·팀) 조합이면 다시 안 부른다 — 뷰(달력/추이/상관/표)를 오갈 때마다
  //* 서버가 그 달을 통째로 다시 집계하던 걸 막는다
  const {
    data,
    loading,
    error,
    notReady, // 구서버 404
    refetch: fetchData,
  } = useCachedResource<CoffinOutDailyResponse>(
    `coffinOutDaily|${ym}|${brandId}|${teamType}`,
    () => {
      const { y, m } = parseYm(ym);
      const lastDay = String(daysInMonth(y, m)).padStart(2, '0');
      // 발인일 축, KST 월 경계로 변환해 전달
      const fromIso = new Date(`${ym}-01T00:00:00+09:00`).toISOString();
      const toIso = new Date(`${ym}-${lastDay}T23:59:59.999+09:00`).toISOString();
      return getDashboardCoffinOutDaily(
        fromIso,
        toIso,
        brandId || undefined,
        teamType || undefined,
      );
    },
    { errorMessage: '발인일 통계를 불러올 수 없습니다.' },
  );

  // 화환 기대치의 기준 — 판매 페이스 응답을 재사용: baseline(공유 상가당 화환)은
  // 완료된 날의 기대비에, cohorts(D-2~D+3 일별 진행도·기대 누적)는 진행 중인 날에.
  // 실패(404 포함)해도 달력은 그대로 동작하고 기대치 표기만 빠진다.
  //* 서버 창이 고정(최근 1년 학습)이라 더 길게 잡아도 값이 거의 같다
  const { data: pacing } = useCachedResource<CoffinOutPacingResponse | null>(
    `coffinOutPacing|${brandId}|${teamType}`,
    async () => {
      try {
        return await getDashboardCoffinOutPacing(
          brandId || undefined,
          teamType || undefined,
        );
      } catch {
        //* 실패(404 포함)해도 달력은 그대로 동작하고 기대치 표기만 빠진다
        return null;
      }
    },
    { ttlMs: PACING_CACHE_TTL_MS },
  );

  const wreathAvg = pacing?.baseline?.avgWreathPerSharedFevent ?? null;
  const cohortByDate = useMemo(
    () => new Map((pacing?.cohorts ?? []).map((c) => [c.date, c])),
    [pacing],
  );

  // 발인 후 3일이 지나야 판매가 사실상 끝난 것 (판매 페이스와 같은 기준)
  const completeCutoff = useMemo(() => {
    const d = getLocalToday();
    d.setDate(d.getDate() - 3);
    return toDateStr(d);
  }, []);

  /** 화환 기대비 % = 실적 ÷ (공유 상가 × 기준 상가당 화환). 기준 없거나 공유 0이면 null */
  const wreathExpectedPct = useCallback(
    (c: CoffinOutDailyRow): number | null => {
      if (wreathAvg === null || wreathAvg <= 0 || c.sharedFeventCount === 0) {
        return null;
      }
      return Math.round(
        (c.wreathCount / (c.sharedFeventCount * wreathAvg)) * 100,
      );
    },
    [wreathAvg],
  );

  const { y, m } = parseYm(ym);
  const lastDay = daysInMonth(y, m);

  // 일자 셀 — 서버가 빈 날 포함 전체를 주지만 방어적으로 빈 행을 채운다
  const cells: DayCell[] = useMemo(() => {
    const byDate = new Map((data?.days ?? []).map((d) => [d.date, d]));
    const list: DayCell[] = [];
    for (let day = 1; day <= lastDay; day++) {
      const key = `${ym}-${String(day).padStart(2, '0')}`;
      list.push({
        ...(byDate.get(key) ?? emptyRow(key)),
        day,
        weekday: new Date(y, m - 1, day).getDay(),
      });
    }
    return list;
  }, [data, ym, y, m, lastDay]);

  const totals = data?.totals;

  const heatValue = useCallback(
    (r: CoffinOutDailyRow): number =>
      heatMetric === 'fevent'
        ? r.feventCount
        : heatMetric === 'wreath'
          ? r.wreathAmount
          : r.giftAmount,
    [heatMetric],
  );
  const heatMax = useMemo(
    () => Math.max(...cells.map(heatValue), 1),
    [cells, heatValue],
  );

  // 요일별 합계 (추이 뷰 보조)
  const weekdayAgg = useMemo(() => {
    const rows = WEEKDAY_LABELS.map((label) => ({
      label,
      feventCount: 0,
      wreathAmount: 0,
      wreathCount: 0,
      giftAmount: 0,
      giftQuantity: 0,
    }));
    for (const c of cells) {
      const r = rows[c.weekday];
      if (!r) continue;
      r.feventCount += c.feventCount;
      r.wreathAmount += c.wreathAmount;
      r.wreathCount += c.wreathCount;
      r.giftAmount += c.giftAmount;
      r.giftQuantity += c.giftQuantity;
    }
    return rows;
  }, [cells]);

  const cellTooltip = (c: DayCell): string => {
    let expectTxt = '';
    const cohort = cohortByDate.get(c.date);
    if (
      c.date > completeCutoff &&
      cohort &&
      cohort.expectedProgress !== null &&
      cohort.expectedSoFarCount !== null
    ) {
      // 진행 중인 날: "진행도 X% 시점 — 보통 N건, 지금 M건"
      const pace =
        cohort.paceRatio !== null
          ? ` (페이스 ${Math.round(cohort.paceRatio * 100)}%)`
          : '';
      expectTxt = ` · 진행 ${(cohort.expectedProgress * 100).toFixed(0)}% — 보통 ${cohort.expectedSoFarCount.toFixed(1)}건, 지금 ${fmt(c.wreathCount)}건${pace}`;
    } else {
      const pct = wreathExpectedPct(c);
      if (pct !== null) expectTxt = ` · 기대비 ${pct}%`;
    }
    return `${m}월 ${c.day}일(${WEEKDAY_LABELS[c.weekday] ?? ''}) · 상가 ${fmt(c.feventCount)}곳(공유 ${fmt(c.sharedFeventCount)}) · 화환 ${fmt(c.wreathAmount)}원(${fmt(c.wreathCount)}건)${expectTxt} · 답례품 ${fmt(c.giftAmount)}원(${fmt(c.giftQuantity)}개·${fmt(c.giftOrderCount)}건)`;
  };

  // ─── 필터 바 (모든 뷰 공통, 차트 위 한 줄) ───
  const filterBar = (
    <div className="flex flex-wrap items-center gap-3">
      {/* 월 내비게이션 */}
      <div className="flex items-center gap-1 rounded-lg bg-white p-1 shadow-sm">
        <button
          onClick={() => setYm(addMonths(ym, -1))}
          className="rounded-md px-2.5 py-1 text-sm text-gray-500 hover:bg-gray-100"
          aria-label="이전 달"
        >
          ◀
        </button>
        <span className="min-w-[110px] text-center text-sm font-semibold text-gray-800">
          {y}년 {m}월
        </span>
        <button
          onClick={() => setYm(addMonths(ym, 1))}
          className="rounded-md px-2.5 py-1 text-sm text-gray-500 hover:bg-gray-100"
          aria-label="다음 달"
        >
          ▶
        </button>
      </div>
      <button
        onClick={() => setYm(ymOf(new Date()))}
        className="rounded-lg bg-white px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm hover:bg-gray-50"
      >
        이번 달
      </button>

      {/* 상조 필터 */}
      <select
        value={brandId}
        onChange={(e) => {
          setBrandId(e.target.value);
          setTeamType(''); // 브랜드가 바뀌면 지사 선택은 무효
        }}
        className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs"
        disabled={brandListStatus !== 'success'}
      >
        <option value="">전체 상조</option>
        {brandListStatus === 'success' &&
          brandList?.map((brand) => (
            <option key={brand._id} value={brand._id}>
              {brand.bugoAgency?.corpName || brand._id}
            </option>
          ))}
      </select>

      {/* 지사(teamType) 필터 — 지사가 등록된 상조를 고른 경우에만 */}
      {brandId && teamTypes.length > 0 && (
        <select
          value={teamType}
          onChange={(e) => setTeamType(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs"
        >
          <option value="">전체 지사</option>
          {teamTypes.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      )}

      {/* 뷰 전환 */}
      <div className="ml-auto flex gap-1">
        {VIEW_LABELS.map((v) => (
          <button
            key={v.key}
            onClick={() => setView(v.key)}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium ${
              view === v.key
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            {v.label}
          </button>
        ))}
      </div>
    </div>
  );

  if (loading && !data) return <CenterLoading />;
  if (notReady) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-6 text-center text-sm text-amber-800">
        발인일 통계 API가 아직 이 서버에 배포되지 않았습니다. 배포 후 다시
        열어주세요.
      </div>
    );
  }
  if (error) return <ErrorBox message={error} onRetry={fetchData} />;
  if (!data || !totals) return null;

  return (
    <div className={`space-y-6 ${loading ? 'pointer-events-none opacity-60' : ''}`}>
      {filterBar}

      {/* KPI 카드 */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-xs text-gray-500">발인 상가</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">
            {fmt(totals.feventCount)}곳
          </p>
          <p className="mt-1 text-xs text-gray-400">
            공유 {fmt(totals.sharedFeventCount)}곳
            {totals.feventCount > 0 &&
              ` (${((totals.sharedFeventCount / totals.feventCount) * 100).toFixed(0)}%)`}{' '}
            · 일평균 {(totals.feventCount / lastDay).toFixed(1)}곳
          </p>
        </div>
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-xs text-gray-500">화환 매출</p>
          <p className="mt-1 text-2xl font-bold text-emerald-600">
            {fmt(totals.wreathAmount)}원
          </p>
          <p className="mt-1 text-xs text-gray-400">
            {fmt(totals.wreathCount)}건
            {totals.feventCount > 0 &&
              ` · 상가당 ${fmtWonCompact(Math.round(totals.wreathAmount / totals.feventCount))}원`}
          </p>
        </div>
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-xs text-gray-500">답례품 매출</p>
          <p className="mt-1 text-2xl font-bold text-indigo-600">
            {fmt(totals.giftAmount)}원
          </p>
          <p className="mt-1 text-xs text-gray-400">
            {fmt(totals.giftQuantity)}개 · {fmt(totals.giftOrderCount)}건
            {totals.feventCount > 0 &&
              ` · 상가당 ${fmtWonCompact(Math.round(totals.giftAmount / totals.feventCount))}원`}
          </p>
        </div>
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-xs text-gray-500">합계 매출 (화환+답례품)</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">
            {fmt(totals.wreathAmount + totals.giftAmount)}원
          </p>
          <p className="mt-1 text-xs text-gray-400">
            {totals.feventCount > 0
              ? `상가당 ${fmtWonCompact(Math.round((totals.wreathAmount + totals.giftAmount) / totals.feventCount))}원`
              : '—'}
          </p>
        </div>
      </div>

      {totals.feventCount === 0 && (
        <div className="rounded-lg bg-white p-4 text-center text-sm text-gray-500 shadow">
          해당 월에 발인 상가가 없습니다.
        </div>
      )}

      {view === 'calendar' && (
        <div className="rounded-lg bg-white p-6 shadow">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="text-sm font-semibold text-gray-800">
                발인일 달력
              </h3>
              <p className="mt-0.5 text-xs text-gray-400">
                색이 진할수록 {HEAT_METRICS.find((h) => h.key === heatMetric)?.label}
                이 큰 날 — 셀에 마우스를 올리면 상세
                {wreathAvg !== null && (
                  <span className="block">
                    화환 기대비 = 실적 ÷ (공유 상가 × 최근 8주 기준{' '}
                    {wreathAvg.toFixed(2)}건) · 최근 날짜(발인 후 3일 미경과)는
                    시간 보정 — "진행 X% · 보통 N건→지금 M건"으로 표기
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-1">
              <span className="mr-1 text-xs text-gray-400">색 기준</span>
              {HEAT_METRICS.map((h) => (
                <button
                  key={h.key}
                  onClick={() => setHeatMetric(h.key)}
                  className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                    heatMetric === h.key
                      ? 'border-indigo-600 bg-indigo-600 text-white'
                      : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {h.label}
                </button>
              ))}
            </div>
          </div>

          {/* 요일 헤더 */}
          <div className="grid grid-cols-7 gap-px">
            {WEEKDAY_LABELS.map((label, wd) => (
              <div
                key={label}
                className={`px-2 py-1.5 text-center text-xs font-medium ${weekdayColor(wd)}`}
              >
                {label}
              </div>
            ))}
          </div>

          {/* 날짜 그리드 — overflow-hidden 금지: 첫 줄 셀 툴팁이 위로 뜬다 */}
          <div className="grid grid-cols-7 gap-px bg-gray-200 ring-1 ring-gray-200">
            {/* 첫 주 앞쪽 빈 칸 */}
            {Array.from({ length: cells[0]?.weekday ?? 0 }, (_, i) => (
              <div key={`lead${i}`} className="min-h-[92px] bg-gray-50" />
            ))}
            {cells.map((c) => {
              const ratio = heatValue(c) / heatMax;
              // 최대 알파 0.7 — 이 램프에선 진회색 글자가 항상 읽힌다 (글자색 스위치 불필요)
              const alpha = heatValue(c) === 0 ? 0 : 0.08 + 0.62 * ratio;
              return (
                <div
                  key={c.date}
                  className="group relative min-h-[92px] bg-white p-1.5"
                  style={
                    alpha > 0
                      ? { backgroundColor: `rgba(${HEAT_RGB}, ${alpha})` }
                      : undefined
                  }
                >
                  <div className="pointer-events-none absolute top-0 left-1/2 z-10 hidden -translate-x-1/2 -translate-y-full rounded bg-gray-800 px-2 py-1 text-[10px] leading-tight whitespace-nowrap text-white shadow group-hover:block">
                    {cellTooltip(c)}
                  </div>
                  <p className="text-xs font-semibold text-gray-800">{c.day}</p>
                  {c.feventCount > 0 && (
                    <div className="mt-1 space-y-0.5 text-[10px] leading-tight text-gray-700">
                      <p className="font-semibold">
                        상가 {fmt(c.feventCount)}
                        <span className="font-normal">
                          (공유 {fmt(c.sharedFeventCount)})
                        </span>
                      </p>
                      {c.wreathAmount > 0 && (
                        <p>
                          화환 {fmtWonCompact(c.wreathAmount)}({c.wreathCount})
                        </p>
                      )}
                      {(() => {
                        const cohort = cohortByDate.get(c.date);
                        const inProgress = c.date > completeCutoff;
                        // 진행 중인 날: "진행도상 보통 N건인데 지금 M건" — 시간 보정된 비교
                        if (
                          inProgress &&
                          cohort &&
                          cohort.expectedProgress !== null &&
                          cohort.expectedSoFarCount !== null
                        ) {
                          return (
                            <p className="font-medium">
                              진행 {(cohort.expectedProgress * 100).toFixed(0)}%
                              · 보통 {cohort.expectedSoFarCount.toFixed(1)}건→
                              지금 {fmt(c.wreathCount)}건
                              {cohort.paceRatio !== null &&
                                ` (${Math.round(cohort.paceRatio * 100)}%)`}
                            </p>
                          );
                        }
                        // 판매가 끝난 날: 최종 기대비
                        const pct = wreathExpectedPct(c);
                        if (pct === null || inProgress) return null;
                        return <p className="font-medium">기대비 {pct}%</p>;
                      })()}
                      {c.giftAmount > 0 && (
                        <p>
                          답례 {fmtWonCompact(c.giftAmount)}({c.giftQuantity}개)
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {/* 마지막 주 뒤쪽 빈 칸 */}
            {Array.from(
              { length: (7 - (((cells[0]?.weekday ?? 0) + lastDay) % 7)) % 7 },
              (_, i) => (
                <div key={`tail${i}`} className="min-h-[92px] bg-gray-50" />
              ),
            )}
          </div>
        </div>
      )}

      {view === 'trend' && (
        <>
          <div className="rounded-lg bg-white p-6 shadow">
            <h3 className="text-sm font-semibold text-gray-800">일별 추이</h3>
            <p className="mt-0.5 mb-4 text-xs text-gray-400">
              지표마다 축이 달라 패널을 나눠 그린다 — 막대에 마우스를 올리면 상세
            </p>
            <TrendPanel
              title="상가 수"
              hex="#6366f1"
              cells={cells}
              month={m}
              value={(c) => c.feventCount}
              fmtValue={(v) => `${fmt(v)}곳`}
            />
            <TrendPanel
              title="화환 매출"
              hex="#10b981"
              cells={cells}
              month={m}
              value={(c) => c.wreathAmount}
              fmtValue={(v) => `${fmt(v)}원`}
              fmtTick={fmtWonCompact}
            />
            <TrendPanel
              title="답례품 매출"
              hex="#3b82f6"
              cells={cells}
              month={m}
              value={(c) => c.giftAmount}
              fmtValue={(v) => `${fmt(v)}원`}
              fmtTick={fmtWonCompact}
              showXAxis
            />
          </div>

          {/* 요일 패턴 */}
          <div className="overflow-hidden rounded-lg bg-white shadow">
            <div className="border-b px-4 py-3">
              <h3 className="text-sm font-semibold text-gray-800">
                요일별 합계
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className={TH_CLASS}>요일</th>
                    <th className={`${TH_CLASS} text-right`}>상가</th>
                    <th className={`${TH_CLASS} text-right`}>화환 매출</th>
                    <th className={`${TH_CLASS} text-right`}>답례품 매출</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {weekdayAgg.map((r, wd) => (
                    <tr key={r.label} className="hover:bg-gray-50">
                      <td className={`${TD_CLASS} font-medium ${weekdayColor(wd)}`}>
                        {r.label}
                      </td>
                      <td className={`${TD_CLASS} text-right`}>
                        {fmt(r.feventCount)}곳
                      </td>
                      <td className={`${TD_CLASS} text-right`}>
                        {fmt(r.wreathAmount)}원
                        <span className="ml-1 text-xs text-gray-400">
                          ({fmt(r.wreathCount)}건)
                        </span>
                      </td>
                      <td className={`${TD_CLASS} text-right`}>
                        {fmt(r.giftAmount)}원
                        <span className="ml-1 text-xs text-gray-400">
                          ({fmt(r.giftQuantity)}개)
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {view === 'corr' && (
        <CoffinOutCorrelation ym={ym} brandId={brandId} teamType={teamType} />
      )}

      {/* 판매 페이스 — 오늘 기준이라 월 내비와 무관 */}
      {view === 'pace' && (
        <CoffinOutPacing brandId={brandId} teamType={teamType} />
      )}

      {view === 'table' && (
        <div className="overflow-hidden rounded-lg bg-white shadow">
          <div className="border-b px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-800">
              일별 상세 ({y}년 {m}월)
            </h3>
          </div>
          <div className="max-h-[560px] overflow-x-auto overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="sticky top-0 z-10 bg-gray-50">
                <tr>
                  <th className={TH_CLASS}>날짜</th>
                  <th className={`${TH_CLASS} text-right`}>상가</th>
                  <th className={`${TH_CLASS} text-right`}>공유 상가</th>
                  <th className={`${TH_CLASS} text-right`}>화환 매출</th>
                  <th className={`${TH_CLASS} text-right`}>화환 건수</th>
                  <th className={`${TH_CLASS} text-right`}>답례품 매출</th>
                  <th className={`${TH_CLASS} text-right`}>답례품 수량</th>
                  <th className={`${TH_CLASS} text-right`}>답례품 주문</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {cells.map((c) => (
                  <tr
                    key={c.date}
                    className={`hover:bg-gray-50 ${
                      c.weekday === 0 || c.weekday === 6 ? 'bg-gray-50/60' : ''
                    }`}
                  >
                    <td className={`${TD_CLASS} font-medium`}>
                      {m}월 {c.day}일{' '}
                      <span className={`text-xs ${weekdayColor(c.weekday)}`}>
                        ({WEEKDAY_LABELS[c.weekday] ?? ''})
                      </span>
                    </td>
                    <td className={`${TD_CLASS} text-right`}>
                      {c.feventCount > 0 ? `${fmt(c.feventCount)}곳` : '-'}
                    </td>
                    <td className={`${TD_CLASS} text-right`}>
                      {c.sharedFeventCount > 0
                        ? `${fmt(c.sharedFeventCount)}곳`
                        : '-'}
                    </td>
                    <td className={`${TD_CLASS} text-right`}>
                      {c.wreathAmount > 0 ? `${fmt(c.wreathAmount)}원` : '-'}
                    </td>
                    <td className={`${TD_CLASS} text-right`}>
                      {c.wreathCount > 0 ? fmt(c.wreathCount) : '-'}
                    </td>
                    <td className={`${TD_CLASS} text-right`}>
                      {c.giftAmount > 0 ? `${fmt(c.giftAmount)}원` : '-'}
                    </td>
                    <td className={`${TD_CLASS} text-right`}>
                      {c.giftQuantity > 0 ? fmt(c.giftQuantity) : '-'}
                    </td>
                    <td className={`${TD_CLASS} text-right`}>
                      {c.giftOrderCount > 0 ? fmt(c.giftOrderCount) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="sticky bottom-0 bg-gray-50 font-medium">
                <tr>
                  <td className={TD_CLASS}>합계</td>
                  <td className={`${TD_CLASS} text-right`}>
                    {fmt(totals.feventCount)}곳
                  </td>
                  <td className={`${TD_CLASS} text-right`}>
                    {fmt(totals.sharedFeventCount)}곳
                  </td>
                  <td className={`${TD_CLASS} text-right`}>
                    {fmt(totals.wreathAmount)}원
                  </td>
                  <td className={`${TD_CLASS} text-right`}>
                    {fmt(totals.wreathCount)}
                  </td>
                  <td className={`${TD_CLASS} text-right`}>
                    {fmt(totals.giftAmount)}원
                  </td>
                  <td className={`${TD_CLASS} text-right`}>
                    {fmt(totals.giftQuantity)}
                  </td>
                  <td className={`${TD_CLASS} text-right`}>
                    {fmt(totals.giftOrderCount)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── 내부 컴포넌트 ───────────────────────────────────

/**
 * 일별 단일 지표 막대 패널 (소형 멀티플 한 칸).
 * 상가 수·화환액·답례품액은 스케일이 달라 한 축에 겹치면 왜곡된다 — 패널을 나눈다.
 */
const TrendPanel: FC<{
  title: string;
  hex: string;
  cells: DayCell[];
  month: number;
  value: (c: DayCell) => number;
  fmtValue: (v: number) => string;
  fmtTick?: (v: number) => string;
  showXAxis?: boolean;
}> = ({ title, hex, cells, month, value, fmtValue, fmtTick = fmt, showXAxis }) => {
  const scale = getNiceScale(Math.max(...cells.map(value), 1), 3);

  return (
    <div className="mb-2">
      <p className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-600">
        <span
          className="inline-block h-2.5 w-2.5 rounded-sm"
          style={{ backgroundColor: hex }}
        />
        {title}
      </p>
      <div className="flex">
        {/* Y축 눈금 */}
        <div className="relative mr-2 h-24 w-10 shrink-0">
          {scale.ticks.map((t) => (
            <span
              key={t}
              className="absolute right-0 translate-y-1/2 text-[10px] leading-none text-gray-400"
              style={{ bottom: `${(t / scale.niceMax) * 100}%` }}
            >
              {fmtTick(t)}
            </span>
          ))}
        </div>
        {/* 플롯 */}
        <div className="relative h-24 flex-1">
          {scale.ticks.map((t) => (
            <div
              key={t}
              className="pointer-events-none absolute inset-x-0 border-t border-gray-100"
              style={{ bottom: `${(t / scale.niceMax) * 100}%` }}
            />
          ))}
          <div className="absolute inset-0 flex items-end gap-px">
            {cells.map((c) => {
              const v = value(c);
              const pct = scale.niceMax > 0 ? (v / scale.niceMax) * 100 : 0;
              return (
                <div
                  key={c.date}
                  className="group relative flex h-full flex-1 flex-col items-center justify-end"
                >
                  <div className="pointer-events-none absolute -top-1 left-1/2 z-10 hidden -translate-x-1/2 -translate-y-full rounded bg-gray-800 px-2 py-1 text-[10px] leading-tight whitespace-nowrap text-white shadow group-hover:block">
                    {month}월 {c.day}일({WEEKDAY_LABELS[c.weekday] ?? ''}) ·{' '}
                    {title} {fmtValue(v)}
                  </div>
                  <div
                    className="w-full rounded-t transition-all duration-300"
                    style={{
                      height: `${pct}%`,
                      minHeight: v > 0 ? 2 : 0,
                      backgroundColor: hex,
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* 공유 X축 — 마지막 패널에만 */}
      {showXAxis && (
        <div className="mt-1 ml-12 flex gap-px">
          {cells.map((c) => (
            <span
              key={c.date}
              className={`flex-1 text-center text-[9px] leading-none ${
                c.weekday === 0 ? 'text-red-400' : 'text-gray-400'
              }`}
            >
              {c.day === 1 || c.day % 5 === 0 ? c.day : ''}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
